import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getAdminUser, isSuperAdmin, logAdminAction } from "@/lib/adminAuth";
import { Save, TestTube, Send, CheckCircle, ArrowLeft } from "lucide-react";
import { MergeFieldsBuilder } from "@/components/admin/MergeFieldsBuilder";
import { SectionsBuilder } from "@/components/admin/SectionsBuilder";
import { SignatureBlockManager } from "@/components/admin/SignatureBlockManager";
import { TemplatePreview } from "@/components/admin/TemplatePreview";
import { TemplateTestModal } from "@/components/admin/TemplateTestModal";
import { safeUUID } from "@/lib/utils";

interface TemplateData {
  id?: string;
  template_name: string;
  category: string;
  template_type: string;
  description: string;
  template_content: string;
  merge_fields: any[];
  sections: any[];
  signature_blocks?: any[];
  related_workflow_ids?: string[];
  is_active?: boolean;
  status: string;
  current_version: number;
  docuseal_template_id?: string;
}

export default function TemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adminUser, setAdminUser] = useState<any>(null);
  const [testModalOpen, setTestModalOpen] = useState(false);
  
  const [template, setTemplate] = useState<TemplateData>({
    template_name: "",
    category: "governance",
    template_type: "resolution",
    description: "",
    template_content: "",
    merge_fields: [],
    sections: [],
    signature_blocks: [],
    related_workflow_ids: [],
    is_active: true,
    status: "draft",
    current_version: 1,
  });
  const [autoSaveTimer, setAutoSaveTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadAdminUser();
    if (id && id !== "new") {
      // Validate UUID before loading
      if (!safeUUID(id)) {
        console.error(`Invalid template ID: "${id}"`);
        toast({
          title: "Invalid Template ID",
          description: "The template ID is invalid.",
          variant: "destructive",
        });
        navigate('/admin/templates');
        return;
      }
      loadTemplate();
    }
  }, [id]);

  // Auto-save functionality
  useEffect(() => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }
    
    const timer = setTimeout(() => {
      if (id && id !== "new") {
        handleAutoSave();
      }
    }, 30000); // 30 seconds
    
    setAutoSaveTimer(timer);
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [template]);

  async function loadAdminUser() {
    const admin = await getAdminUser();
    setAdminUser(admin);
  }

  async function loadTemplate() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('document_templates')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (data) {
        setTemplate({
          ...data,
          merge_fields: Array.isArray(data.merge_fields) ? data.merge_fields : [],
          sections: Array.isArray(data.sections) ? data.sections : [],
          signature_blocks: [],
          related_workflow_ids: [],
        });
      }
    } catch (error: any) {
      console.error('Error loading template:', error);
      toast({
        title: "Error",
        description: "Failed to load template",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveDraft() {
    try {
      setSaving(true);
      
      if (id && id !== "new") {
        // Update existing
        const { error } = await supabase
          .from('document_templates')
          .update({
            ...template,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id);

        if (error) throw error;
        
        await logAdminAction('updated_template_draft', 'template', id, { template_name: template.template_name });
      } else {
        // Create new
        const { data: { user } } = await supabase.auth.getUser();
        const { data, error } = await supabase
          .from('document_templates')
          .insert({
            ...template,
            created_by: user?.id,
            status: 'draft',
          })
          .select()
          .single();

        if (error) throw error;
        
        await logAdminAction('created_template_draft', 'template', data.id, { template_name: template.template_name });
        navigate(`/admin/templates/${data.id}/edit`);
      }

      toast({
        title: "Draft Saved",
        description: "Template draft has been saved",
      });
    } catch (error: any) {
      console.error('Error saving template:', error);
      toast({
        title: "Error",
        description: "Failed to save template",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleAutoSave() {
    try {
      const { error } = await supabase
        .from('document_templates')
        .update({
          ...template,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
      
      console.log('Auto-saved template at', new Date().toLocaleTimeString());
    } catch (error: any) {
      console.error('Auto-save error:', error);
    }
  }

  async function handlePublish() {
    if (isSuperAdmin(adminUser)) {
      // Super admin can publish immediately
      try {
        setSaving(true);
        const newVersion = (template.current_version || 1) + 1;
        
        const { error } = await supabase
          .from('document_templates')
          .update({
            ...template,
            status: 'published',
            current_version: newVersion,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id);

        if (error) throw error;
        
        await logAdminAction('published_template', 'template', id, { 
          template_name: template.template_name,
          version: newVersion 
        });

        toast({
          title: "Template Published",
          description: `Template published as version ${newVersion}`,
        });
        
        navigate('/admin/templates');
      } catch (error: any) {
        console.error('Error publishing template:', error);
        toast({
          title: "Error",
          description: "Failed to publish template",
          variant: "destructive",
        });
      } finally {
        setSaving(false);
      }
    }
  }

  if (loading) {
    return <div className="container mx-auto p-6">Loading template...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/templates')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {id === 'new' ? 'Create Template' : 'Edit Template'}
            </h1>
            <p className="text-muted-foreground">
              {template.status === 'draft' ? 'Draft' : `Version ${template.current_version}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button variant="outline" onClick={() => setTestModalOpen(true)}>
            <TestTube className="h-4 w-4 mr-2" />
            Test
          </Button>
          <Button onClick={handlePublish} disabled={saving}>
            {isSuperAdmin(adminUser) ? (
              <><CheckCircle className="h-4 w-4 mr-2" />Publish</>
            ) : (
              <><Send className="h-4 w-4 mr-2" />Request Approval</>
            )}
          </Button>
        </div>
      </div>

      {/* Two-Panel Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Configuration */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Template Name</Label>
                <Input
                  id="name"
                  value={template.template_name}
                  onChange={(e) => setTemplate({ ...template, template_name: e.target.value })}
                  placeholder="Enter template name"
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={template.category}
                  onValueChange={(value) => setTemplate({ ...template, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member_management">Member Management</SelectItem>
                    <SelectItem value="asset_transfers">Asset Transfers</SelectItem>
                    <SelectItem value="governance">Governance</SelectItem>
                    <SelectItem value="contributions">Contributions</SelectItem>
                    <SelectItem value="estate_planning">Estate Planning</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                    <SelectItem value="legal">Legal</SelectItem>
                    <SelectItem value="operational">Operational</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="type">Document Type</Label>
                <Select
                  value={template.template_type}
                  onValueChange={(value) => setTemplate({ ...template, template_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resolution">Resolution</SelectItem>
                    <SelectItem value="agreement">Agreement</SelectItem>
                    <SelectItem value="form">Form</SelectItem>
                    <SelectItem value="letter">Letter</SelectItem>
                    <SelectItem value="consent">Consent</SelectItem>
                    <SelectItem value="designation">Designation</SelectItem>
                    <SelectItem value="deed">Deed</SelectItem>
                    <SelectItem value="bill_of_sale">Bill of Sale</SelectItem>
                    <SelectItem value="assignment">Assignment</SelectItem>
                    <SelectItem value="minutes">Minutes</SelectItem>
                    <SelectItem value="agenda">Agenda</SelectItem>
                    <SelectItem value="receipt">Receipt</SelectItem>
                    <SelectItem value="procedure">Procedure Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={template.description}
                  onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                  placeholder="Describe this template's purpose"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="docuseal_template_id">DocuSeal Template ID (Optional)</Label>
                <Input
                  id="docuseal_template_id"
                  value={template.docuseal_template_id || ''}
                  onChange={(e) => setTemplate({ ...template, docuseal_template_id: e.target.value })}
                  placeholder="Enter DocuSeal template ID for pre-configured signature fields"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  If provided, signature fields will use the visual positions from your DocuSeal template instead of automatic positioning
                </p>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="fields">
            <div className="overflow-x-auto">
              <TabsList className="inline-flex w-auto min-w-max md:grid md:w-full md:grid-cols-3">
                <TabsTrigger value="fields">Merge Fields</TabsTrigger>
                <TabsTrigger value="sections">Sections</TabsTrigger>
                <TabsTrigger value="signatures">Signatures</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="fields">
              <MergeFieldsBuilder
                fields={template.merge_fields}
                onChange={(fields) => setTemplate({ ...template, merge_fields: fields })}
              />
            </TabsContent>
            <TabsContent value="sections">
              <SectionsBuilder
                sections={template.sections}
                mergeFields={template.merge_fields}
                onChange={(sections) => setTemplate({ ...template, sections: sections })}
              />
            </TabsContent>
            <TabsContent value="signatures">
              <SignatureBlockManager
                blocks={template.signature_blocks || []}
                onChange={(blocks) => setTemplate({ ...template, signature_blocks: blocks })}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Panel - Live Preview */}
        <div className="sticky top-6">
          <TemplatePreview
            template={template}
          />
        </div>
      </div>

      {/* Test Modal */}
      <TemplateTestModal
        open={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        template={template}
      />
    </div>
  );
}
